/**
 * property-view — decisions pures del calendari v2 sobre les propietats (pestanyes o totes) i el
 * rang de dades (plans/dia-a-dia-calendari-i-whatsapp.md §2). Sense React ni navegador: els tests
 * (environment node) les proven directament.
 */

import { addMonths, addYears, format, startOfMonth, subMonths } from 'date-fns';
import { STORAGE_KEYS, storage } from '@/lib/storage';

/** L'hotel (Beds24 18770): sempre la primera pestanya i el primer bloc de «Totes». */
export const HOTEL_PROPERTY_ID = 18770;

/** Pestanyes = una propietat alhora; Totes = totes seguides en una sola graella. */
export type PropertyView = 'tabs' | 'all';

/** El mínim d'una propietat per ordenar-la. */
export interface OrderableProperty {
  beds24_property_id: number;
  nom: string;
}

/**
 * Ordre de les propietats al calendari: la 18770 primer, després per nom. Quan la 18770 es digui
 * «HOTEL GUILLEUMES MONTSERRAT», per ordre alfabètic quedaria darrere de CAL NOTARI i FLAM (§2.3).
 */
export function sortPropertiesForCalendar<T extends OrderableProperty>(properties: readonly T[]): T[] {
  return [...properties].sort((a, b) => {
    const aHotel = a.beds24_property_id === HOTEL_PROPERTY_ID;
    const bHotel = b.beds24_property_id === HOTEL_PROPERTY_ID;
    if (aHotel !== bHotel) return aHotel ? -1 : 1;
    return (a.nom || '').localeCompare(b.nom || '', 'ca', { numeric: true });
  });
}

/**
 * La propietat de la pestanya: la desada si és d'una propietat activa; si no (no n'hi ha, és
 * d'una propietat retirada o un valor estrany), la primera de l'ordre. Sense propietats → `null`.
 */
export function resolveSelectedPropertyId(
  saved: unknown,
  orderedPropertyIds: readonly number[],
): number | null {
  const id = typeof saved === 'string' ? Number.parseInt(saved, 10) : saved;
  if (typeof id === 'number' && orderedPropertyIds.includes(id)) return id;
  return orderedPropertyIds[0] ?? null;
}

/** Valor desat → vista. Qualsevol cosa que no sigui `all` és `tabs` (el per defecte). */
export function parsePropertyView(value: unknown): PropertyView {
  return value === 'all' ? 'all' : 'tabs';
}

/** Llegeix la vista desada. `storage` ja tanca `localStorage` en un `try/catch`. */
export function readPropertyView(): PropertyView {
  return parsePropertyView(storage.get<unknown>(STORAGE_KEYS.CALENDAR_PROPERTY_VIEW, 'tabs'));
}

export function savePropertyView(view: PropertyView): void {
  storage.set(STORAGE_KEYS.CALENDAR_PROPERTY_VIEW, view);
}

/** La pestanya desada, tal com és (es valida amb `resolveSelectedPropertyId`). */
export function readSavedPropertyId(): number | null {
  const saved = storage.get<unknown>(STORAGE_KEYS.CALENDAR_V2_PROPERTY_ID, null);
  const id = typeof saved === 'string' ? Number.parseInt(saved, 10) : saved;
  return typeof id === 'number' && Number.isFinite(id) ? id : null;
}

export function saveSelectedPropertyId(propertyId: number): void {
  storage.set(STORAGE_KEYS.CALENDAR_V2_PROPERTY_ID, propertyId);
}

/**
 * Rang de dades de la graella: un mes enrere i tretze endavant, **arrodonit al mes** perquè la clau
 * de la query no canviï a cada setmana (abans es tornaven a baixar dos anys a cada clic, §2.4).
 *
 * `from` = dia 1 del mes anterior al de `date`. Abans era un any enrere, i el passat era el 90 % del
 * pes de la resposta de `GET /calendar/bookings` (3.122 reserves i 5,2 MB a l'hotel; amb un mes en
 * queden 556): mentre el backend la serveix no atén res més, i les portes tenen un timeout de 3 s.
 * Un mes sencer, i no menys, perquè anar una setmana enrere des de qualsevol dia del mes caigui dins
 * del que ja hi havia carregat i la graella no es quedi buida mentre arriba el rang nou.
 * Més enrere d'això, la graella ho demana en arribar-hi (clau nova) i els formularis avisen que
 * aquells dies no són carregats (`useLoadedOccupancy`).
 *
 * `to` = dia 1 del mes següent, un any endavant: cobreix els 14 dies visibles encara que passin al
 * mes següent. Dates locals (`yyyy-MM-dd`), no `toISOString()`, que a mitjanit d'aquí dona el dia
 * anterior en UTC.
 */
export function stableDateRange(date: Date): { from: string; to: string } {
  const monthStart = startOfMonth(date);
  return {
    from: format(subMonths(monthStart, 1), 'yyyy-MM-dd'),
    to: format(addYears(addMonths(monthStart, 1), 1), 'yyyy-MM-dd'),
  };
}

/** Estat d'un bloc de la graella (una propietat): les seves unitats i reserves. */
export type CalendarBlockState = 'ready' | 'loading' | 'error';

/**
 * `ready` quan hi ha unitats i reserves (encara que siguin les d'abans, `placeholderData`);
 * `error` si falta alguna de les dues i la seva query ha fallat. Abans només es mirava si hi havia
 * dades, i una query fallida deixava el «Carregant» per sempre.
 */
export function calendarBlockState(input: {
  hasUnits: boolean;
  hasBookings: boolean;
  unitsError: boolean;
  bookingsError: boolean;
}): CalendarBlockState {
  if (input.hasUnits && input.hasBookings) return 'ready';
  if ((!input.hasUnits && input.unitsError) || (!input.hasBookings && input.bookingsError)) {
    return 'error';
  }
  return 'loading';
}
